import styles from './Ideas.module.css';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { PERMISSIONS } from '../../shared/permission';
import PermissionChecker from '../UI/PermissionChecker/PermissionChecker';
import { ideaService } from '../../services/ideas.service';
import TableGrid from '../UI/Table/TableGrid/TableGrid';
import DateTime from '../UI/Table/DateTime/DateTime';
import ColoredText from '../UI/Table/ColoredText/ColoredText';
import UserCell from '../UI/Table/UserCell/UserCell';
import { useDispatch, useSelector } from 'react-redux';
import { setIdeasTableFilters, selectIdeasTableFilters } from '../../redux/slices/tableStatesSlice';
import IconButton from '../UI/IconButton/IconButton';
import Input from '../UI/Input/Input';
import Select from '../UI/Select/Select';
import Button from '../UI/Button/Button';
import { IoIosRefresh } from 'react-icons/io';
import { tagService } from '../../services/tags.service';
import IdeasCreateForm from './IdeasCreateForm/IdeasCreateForm';
import { Link } from 'react-router-dom';

function Ideas() {
    const { t } = useTranslation('common');
    const ideasTableFilters = useSelector(selectIdeasTableFilters);
    const [tagsList, setTagsList] = useState([]);
    const [search, setSearch] = useState('');
    const [ideas, setIdeas] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [tableFilters, setTableFilters] = useState({
        search: '',
        status: '',
        tags: [],
        creator: '',
        page: 0,
        pageSize: ideasTableFilters.pageSize
    });
    const [refreshKey, setRefreshKey] = useState(0);
    const dispatch = useDispatch();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    useEffect(() => {
        const loadTags = async () => {
            try {
                const response = await tagService.getTagsAll();
                setTagsList(response.tags ?? []);
            } catch (error) {
                console.log(error);
            }
        };
        loadTags();
    }, []);

    useEffect(() => {
        if (search.length >= 3 || search.length === 0) {
            setTableFilters(prev => ({ ...prev, search }));
        }
    }, [search]);

    const colorMap = {
        'ready_to_review': 'green',
        'draft': 'yellow',
        'archived': 'gray',
    }

    const statuses = [
        { value: '', label: t('ideas.statuses.all') },
        { value: 'ready_to_review', label: t('ideas.statuses.ready_to_review') },
        { value: 'draft', label: t('ideas.statuses.draft') },
        { value: 'archived', label: t('ideas.statuses.archived') },
    ]

    const refreshTable = () => {
        setPaginationModel((m) => ({ ...m, page: 0 }));
        setRefreshKey(prev => prev + 1);
    };

    const handleIdeaCreated = () => {
        refreshTable();
    };

    const renderStatus = (params) => {
        return <ColoredText value={t(`ideas.statuses.${params.value}`)} colorMap={colorMap} colorCondition={params.value} />;
    };

    const renderDateTime = (params) => {
        return <DateTime date={params.value} />;
    };

    const renderUserCell = (params) => {
        return <UserCell user={params.value} />;
    };

    const renderTitle = (params) => {
        return <Link to={`/crm/ideas/${params.id}`} className={styles.titleLink}>{params.value}</Link>;
    };

    const columns = [
        { field: 'id', headerName: '№', width: 40, flex: 0, sortable: false },
        { field: 'title', headerName: t('ideas.title'), width: 250, flex: 0, sortable: false, renderCell: renderTitle },
        { field: 'description', headerName: t('ideas.description'), width: 250, flex: 1, sortable: false },
        { field: 'status', headerName: t('ideas.status'), width: 150, flex: 0, sortable: false, renderCell: renderStatus },
        { field: 'tags', headerName: t('ideas.tags_label'), width: 150, flex: 0, sortable: false },
        { field: 'created_by', headerName: t('ideas.creator'), width: 150, flex: 0, sortable: false, renderCell: renderUserCell },
        { field: 'created_at', headerName: t('ideas.created_at'), width: 150, flex: 0, sortable: false, renderCell: renderDateTime },
    ]

    const handleTableFiltersChange = (key, value) => {
        setTableFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        let alive = true;

        const load = async () => {
            setIsLoading(true);
            try {
                const response = await ideaService.getIdeas({
                    queryParams: {
                        search: tableFilters.search,
                        status: tableFilters.status,
                        tags: tableFilters.tags,
                        creator: tableFilters.creator,
                        page: paginationModel.page + 1,
                        limit: paginationModel.pageSize,
                    }
                });
                if (alive) {
                    const ideasTransformed = response.rows?.map(idea => {
                        return {
                            ...idea
                        }
                    });
                    setIdeas(ideasTransformed ?? []);
                    setTotal(response.total ?? 0);
                }
            } catch (error) {
                console.log(error);
            } finally {
                if (alive) setIsLoading(false);
            }
        };
        load();
    }, [paginationModel.page, paginationModel.pageSize, tableFilters.search, tableFilters.status, tableFilters.tags, tableFilters.creator, t, refreshKey]);

    return (
        <div className={styles.container}>
            <div className={styles.tableWrapper}>
                <PermissionChecker permissions={[PERMISSIONS.IDEAS.VIEW]}>
                    <div className={styles.headerContainer}>
                        <IconButton onClick={refreshTable} variant="secondary">
                            <IoIosRefresh />
                        </IconButton>
                        <div className={styles.filtersContainer}>
                            <Input
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPaginationModel((m) => ({ ...m, page: 0 }));
                                }}
                                placeholder={t('ideas.search_placeholder')}
                                className={styles.input}
                            />
                            <Select
                                options={statuses}
                                value={tableFilters.status}
                                onChange={(e) => {
                                    handleTableFiltersChange('status', e.target.value);
                                    setPaginationModel((m) => ({ ...m, page: 0 }));
                                }}
                                wrapperClassName={styles.selectFilterWrapper}
                            />
                        </div>
                        <PermissionChecker permissions={[PERMISSIONS.IDEAS.CREATE]}>
                            <Button
                                variant="primary"
                                onClick={() => setIsCreateModalOpen(true)}
                                className={styles.createButton}
                            >
                                {t('ideas.create')}
                            </Button>
                        </PermissionChecker>
                    </div>
                    <TableGrid
                        noRowsText={t('ideas.no_rows')}
                        columns={columns}
                        rows={ideas}
                        loading={isLoading}
                        rowCount={total}
                        rowHeight={44}
                        paginationMode='server'
                        paginationModel={paginationModel}
                        pageSizeOptions={[10, 25, 50]}
                        sortingMode='server'
                        onPaginationModelChange={(model) => {
                            setPaginationModel(model);
                            dispatch(setIdeasTableFilters({
                                ...tableFilters,
                                page: model.page,
                                pageSize: model.pageSize
                            }));
                        }}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableColumnSort
                    />
                </PermissionChecker>
            </div>
            <PermissionChecker permissions={[PERMISSIONS.IDEAS.CREATE]}>
                {isCreateModalOpen && (
                    <IdeasCreateForm
                        isOpen={isCreateModalOpen}
                        onClose={() => setIsCreateModalOpen(false)}
                        onSuccess={handleIdeaCreated}
                        tagsList={tagsList}
                        statuses={statuses.filter(status => status.value !== '' && status.value !== 'archived')}
                    />
                )}
            </PermissionChecker>
        </div>
    )
}

export default Ideas;